/**
 * BVG SLP — Animation Engine
 * Scroll-triggered reveals, micro-interactions, and Apple-quality motion.
 */

(function () {
  'use strict';

  /* ── Config ────────────────────────────────────────────── */
  const REVEAL_THRESHOLD = 0.12;   // 12% of element must be visible
  const STAGGER_CLASSES  = ['product-grid', 'collection-list', 'blog-posts', 'resource-list'];

  /* ── Respect reduced-motion preference ─────────────────── */
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ================================================================
     1. SCROLL-REVEAL  (IntersectionObserver)
     ================================================================ */
  function initReveal() {
    if (prefersReducedMotion) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('bvg-revealed');
            observer.unobserve(entry.target); // fire once
          }
        });
      },
      { threshold: REVEAL_THRESHOLD, rootMargin: '0px 0px -40px 0px' }
    );

    // Tag standard Shopify section containers
    document.querySelectorAll(
      '.shopify-section, ' +
      '.hero, ' +
      '.section, ' +
      'featured-product, ' +
      '.featured-blog-posts, ' +
      '.collection-list, ' +
      '.product-list-section'
    ).forEach((el) => {
      if (!el.classList.contains('bvg-reveal')) {
        el.classList.add('bvg-reveal');
      }
      observer.observe(el);
    });

    // Product cards — stagger within grids
    document.querySelectorAll('.product-grid, .collection-grid, .resource-list').forEach((grid) => {
      grid.classList.add('bvg-stagger');
      observer.observe(grid);
    });
  }

  /* ================================================================
     2. HEADER SCROLL STATE
     Adds `.is-scrolled` to header when user scrolls past 60px.
     ================================================================ */
  function initHeaderScroll() {
    const header = document.querySelector('header-component, header');
    if (!header) return;

    let ticking = false;

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (window.scrollY > 60) {
            header.setAttribute('data-scrolled', '');
          } else {
            header.removeAttribute('data-scrolled');
          }
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ================================================================
     3. PRODUCT CARD — TILT EFFECT (subtle 3-D on hover)
     ================================================================ */
  function initCardTilt() {
    if (prefersReducedMotion) return;
    if ('ontouchstart' in window) return; // skip on touch

    const MAX_TILT = 5; // degrees

    document.querySelectorAll('.product-card, .card-product').forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const rect   = card.getBoundingClientRect();
        const cx     = rect.left + rect.width  / 2;
        const cy     = rect.top  + rect.height / 2;
        const dx     = (e.clientX - cx) / (rect.width  / 2);
        const dy     = (e.clientY - cy) / (rect.height / 2);
        const rotateX = -dy * MAX_TILT;
        const rotateY =  dx * MAX_TILT;

        card.style.transform =
          `translateY(-6px) perspective(600px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = '';
        card.style.transition = 'transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94)';
        setTimeout(() => { card.style.transition = ''; }, 400);
      });
    });
  }

  /* ================================================================
     4. ANNOUNCEMENT BAR — Pause shimmer on hover
     ================================================================ */
  function initAnnouncementBar() {
    const bar = document.querySelector('.announcement-bar');
    if (!bar) return;
    bar.addEventListener('mouseenter', () => bar.style.setProperty('--shimmer-play', 'paused'));
    bar.addEventListener('mouseleave', () => bar.style.setProperty('--shimmer-play', 'running'));
  }

  /* ================================================================
     5. BUTTON RIPPLE — Material-inspired tap feedback
     ================================================================ */
  function initRipple() {
    if (prefersReducedMotion) return;

    const rippleStyle = document.createElement('style');
    rippleStyle.textContent = `
      .bvg-ripple-host { position: relative; overflow: hidden; }
      .bvg-ripple {
        position: absolute;
        border-radius: 50%;
        transform: scale(0);
        animation: bvg-ripple-anim 0.55s linear;
        background: rgba(255,255,255,0.35);
        pointer-events: none;
      }
      @keyframes bvg-ripple-anim {
        to { transform: scale(4); opacity: 0; }
      }
    `;
    document.head.appendChild(rippleStyle);

    document.querySelectorAll('.button--primary, .btn-primary').forEach((btn) => {
      btn.classList.add('bvg-ripple-host');
      btn.addEventListener('click', (e) => {
        const rect   = btn.getBoundingClientRect();
        const size   = Math.max(rect.width, rect.height);
        const ripple = document.createElement('span');
        ripple.className = 'bvg-ripple';
        Object.assign(ripple.style, {
          width:  size + 'px',
          height: size + 'px',
          left:   (e.clientX - rect.left - size / 2) + 'px',
          top:    (e.clientY - rect.top  - size / 2) + 'px',
        });
        btn.appendChild(ripple);
        ripple.addEventListener('animationend', () => ripple.remove());
      });
    });
  }

  /* ================================================================
     6. SMOOTH COUNTER — animate numeric values when visible
     ================================================================ */
  function initCounters() {
    if (prefersReducedMotion) return;

    const counterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el      = entry.target;
          const target  = parseFloat(el.dataset.count || el.textContent.replace(/[^0-9.]/g, ''));
          const suffix  = el.dataset.suffix || '';
          const duration = 1200;
          const start    = performance.now();

          function step(now) {
            const progress = Math.min((now - start) / duration, 1);
            // ease-out-expo
            const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
            el.textContent = Math.round(eased * target).toLocaleString() + suffix;
            if (progress < 1) requestAnimationFrame(step);
          }

          requestAnimationFrame(step);
          counterObserver.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );

    document.querySelectorAll('[data-count]').forEach((el) => counterObserver.observe(el));
  }

  /* ================================================================
     7. HERO PARALLAX — subtle depth on scroll
     ================================================================ */
  function initParallax() {
    if (prefersReducedMotion) return;
    if ('ontouchstart' in window) return;

    const hero = document.querySelector('.hero, [data-section-type="hero"]');
    if (!hero) return;

    const bg = hero.querySelector('img, video, .hero__media');
    if (!bg) return;

    window.addEventListener('scroll', () => {
      const scrolled = window.scrollY;
      const limit    = hero.offsetHeight;
      if (scrolled > limit) return;
      const offset = scrolled * 0.3;
      bg.style.transform = `translateY(${offset}px)`;
    }, { passive: true });
  }

  /* ================================================================
     8. CART ICON BOUNCE on add-to-cart
     ================================================================ */
  function initCartBounce() {
    if (prefersReducedMotion) return;

    const cartBounceStyle = document.createElement('style');
    cartBounceStyle.textContent = `
      @keyframes bvg-cart-bounce {
        0%,100% { transform: scale(1); }
        30%      { transform: scale(1.4); }
        60%      { transform: scale(0.9); }
        80%      { transform: scale(1.1); }
      }
      .bvg-cart-bounce { animation: bvg-cart-bounce 0.6s cubic-bezier(0.34,1.56,0.64,1); }
    `;
    document.head.appendChild(cartBounceStyle);

    document.addEventListener('dispatch:cart:add', () => triggerCartBounce());
    document.addEventListener('cart:item:added',   () => triggerCartBounce());

    function triggerCartBounce() {
      const cartIcon = document.querySelector('.header__cart, [href="/cart"], .cart-icon');
      if (!cartIcon) return;
      cartIcon.classList.remove('bvg-cart-bounce');
      void cartIcon.offsetWidth; // force reflow
      cartIcon.classList.add('bvg-cart-bounce');
      cartIcon.addEventListener('animationend', () => {
        cartIcon.classList.remove('bvg-cart-bounce');
      }, { once: true });
    }
  }

  /* ================================================================
     9. IMAGE LAZY LOAD — fade-in when loaded
     ================================================================ */
  function initImageFade() {
    if (prefersReducedMotion) return;

    const imgStyle = document.createElement('style');
    imgStyle.textContent = `
      img.bvg-img-lazy { opacity: 0; transition: opacity 0.4s ease; }
      img.bvg-img-lazy.bvg-img-loaded { opacity: 1; }
    `;
    document.head.appendChild(imgStyle);

    document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.classList.add('bvg-img-lazy');
      if (img.complete) {
        img.classList.add('bvg-img-loaded');
      } else {
        img.addEventListener('load', () => img.classList.add('bvg-img-loaded'));
      }
    });
  }

  /* ================================================================
     10. SMOOTH ANCHOR LINKS
     ================================================================ */
  function initSmoothAnchors() {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', (e) => {
        const target = document.querySelector(anchor.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ================================================================
     INIT — run after DOM ready
     ================================================================ */
  function init() {
    // Content remains visible without scroll observers.
    initHeaderScroll();
    initCardTilt();
    initAnnouncementBar();
    initRipple();
    initCounters();
    initParallax();
    initCartBounce();
    // Native lazy loading keeps images visible without load-event gating.
    initSmoothAnchors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section re-render (theme editor)
  document.addEventListener('shopify:section:load', () => {
    // Content remains visible without scroll observers.
    initCardTilt();
    initRipple();
  });

})();
